import "../styles/globals.css";

import { useEffect, useMemo, useState } from "react";
import type { CheckoutSession } from "../domain/entities/CheckoutSession";
import { checkoutCopy } from "../presentation/i18n/checkout/es";
import { AppRouter } from "../presentation/routes/AppRouter";
import { ApplicationProvider } from "./providers/ApplicationProvider";
import { StaticSessionTokenDecoderAdapter } from "../infrastructure/adapters/session/StaticSessionTokenDecoderAdapter";

export const App = () => {
  const [session, setSession] = useState<CheckoutSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDecoding, setIsDecoding] = useState(true);

  const decoder = useMemo(() => new StaticSessionTokenDecoderAdapter(), []);
  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const useMocks = useMemo(() => {
    const flag = searchParams.get("useMocks");

    if (flag) {
      return flag.toLowerCase() === "true";
    }

    return process.env.REACT_APP_USE_API_MOCKS === "true";
  }, [searchParams]);

  useEffect(() => {
    const token = searchParams.get("token");

    decoder
      .decode(token ?? "")
      .then((decodedSession) => {
        setSession(decodedSession);
        setError(null);
      })
      .catch((err) => {
        setSession(null);
        setError(err instanceof Error ? err.message : checkoutCopy.order.errorFallback);
      })
      .finally(() => {
        setIsDecoding(false);
      });
  }, [decoder, searchParams]);

  if (isDecoding) {
    return <div className="prw-status-message">{checkoutCopy.order.loading}</div>;
  }

  if (!session) {
    return (
      <div className="prw-status-message" role="alert">
        {error ?? checkoutCopy.order.errorFallback}
      </div>
    );
  }

  return (
    <ApplicationProvider session={session} useMocks={useMocks}>
      <AppRouter />
    </ApplicationProvider>
  );
};
